import { SearchIcon } from 'lucide-react';
import { type RefObject, useMemo, useRef, useState } from 'react';

import type { SkuDTO } from '@invenco/products-interface';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/core/combobox';
import { InputGroupAddon } from '@/components/core/input-group';
import { useGateways } from '@/gateways/GatewayProvider';
import type { GetSkusQuery } from '@/gateways/impl/ProductsGateway';
import { useAutoCompleteQuery } from '@/shared/hooks/queries';

import { SkuBadge } from './sku-badge';

type SkuComboboxProps = {
  /**
   * The selected SKU DTO which should be managed as a controlled value with onSelect.
   *
   * NOTE: programmatically initialising or changing this does not update the search input with
   * the description to suite. Given no actual use-case does this, we've opted to not support this.
   * If there is a use-case that needs this, support for it will need to be added here likely by
   * internally controlling the search value further.
   */
  selectedSku?: SkuDTO | null;
  queryParams?: GetSkusQuery;
  className?: string;
  portalContainerRef?: RefObject<HTMLDivElement | null>;
  onSelect: (sku: SkuDTO) => void;
  onClear?: () => void;
  onCancel?: () => void;
};

type SkuOption = {
  value: string;
  label: string;
  description: string | null;
  sku: SkuDTO;
};

function SkuOptionContent({ option }: { option: SkuOption }) {
  return (
    <div className="flex max-w-full min-w-0 items-center gap-2 overflow-hidden">
      <SkuBadge name={option.label} type={option.sku.type} />
      {option.description ? <span className="truncate text-sm">{option.description}</span> : null}
    </div>
  );
}

export function SkuCombobox({
  selectedSku,
  queryParams = {},
  className,
  portalContainerRef,
  onSelect,
  onClear,
  onCancel,
}: SkuComboboxProps) {
  const { productsGateway } = useGateways();
  const [isOpen, setIsOpen] = useState(false);
  const ignoreNextCloseRef = useRef(false);

  const {
    options: matchingSkus,
    onSearch,
    isLoading,
  } = useAutoCompleteQuery({
    parentKey: 'seller-sku-combobox-skus',
    queryParams,
    query: (input, { signal }) => productsGateway.getSkus(input, { signal }),
    enabled: true,
  });

  const options = useMemo<SkuOption[]>(
    () =>
      matchingSkus.map((sku) => ({
        value: sku.id,
        label: sku.name,
        description: sku.description,
        sku,
      })),
    [matchingSkus],
  );

  return (
    <Combobox
      open={isOpen}
      onOpenChange={(nextOpen) => {
        setIsOpen(nextOpen);

        if (onCancel && !nextOpen) {
          queueMicrotask(() => {
            if (ignoreNextCloseRef.current) {
              ignoreNextCloseRef.current = false;
              return;
            }

            onCancel();
          });
        }
      }}
      filteredItems={options}
      items={options}
      itemToStringLabel={(value) =>
        options.find((option) => option.value === value)?.description ?? ''
      }
      onInputValueChange={(value, eventDetails) => {
        if (eventDetails.reason === 'item-press') {
          return;
        }

        if (typeof value === 'string') {
          onSearch(value);
        }

        if (
          selectedSku &&
          (eventDetails.reason === 'input-change' ||
            eventDetails.reason === 'input-clear' ||
            eventDetails.reason === 'clear-press')
        ) {
          onClear?.();
        }
      }}
      onValueChange={(value) => {
        const selectedValue = typeof value === 'string' ? value : undefined;
        if (!selectedValue) return;

        const selectedOption = options.find((option) => option.value === selectedValue);
        if (!selectedOption) return;

        ignoreNextCloseRef.current = true;
        onSelect(selectedOption.sku);
      }}
    >
      <ComboboxInput
        autoFocus={!!onCancel}
        className={className}
        placeholder={selectedSku ? '' : 'Search by SKU or description'}
        aria-label="SKU"
        showClear
        onKeyDown={(event) => {
          if (!onCancel || event.key !== 'Escape') return;

          event.preventDefault();
          if (isOpen) {
            ignoreNextCloseRef.current = true;
            setIsOpen(false);
            return;
          }

          onCancel();
        }}
      >
        <InputGroupAddon align="inline-start">
          <SearchIcon className="size-3.5" />
        </InputGroupAddon>
        {selectedSku ? (
          <span
            className="order-first ml-2.5 flex max-w-[45%] min-w-0 shrink items-center justify-start truncate"
            title={selectedSku.name}
            onMouseDown={(event) => {
              event.preventDefault();
              event.currentTarget.parentElement?.querySelector('input')?.focus();
            }}
          >
            <SkuBadge name={selectedSku.name} type={selectedSku.type} />
          </span>
        ) : null}
      </ComboboxInput>
      <ComboboxContent portalContainer={portalContainerRef}>
        <ComboboxEmpty>{isLoading ? 'Loading SKUs...' : 'No matching SKUs'}</ComboboxEmpty>
        <ComboboxList>
          {(option: SkuOption) => (
            <ComboboxItem key={option.value} value={option.value}>
              <SkuOptionContent option={option} />
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
